import { useEffect, useRef, useState } from 'react';
import { api, openEventStream } from './api.js';
import { appConfig } from './app-config.js';

/**
 * Watching a job.
 *
 * Streams over SSE where the deployment allows it and falls back to
 * polling where it does not. The fallback is not a nicety: behind a proxy
 * with a short idle timeout that cannot be raised, a stream fails
 * intermittently, and intermittent failure is worse than consistently
 * slower updates because users cannot tell it from a broken tool.
 *
 * Both paths converge on the same state shape, so nothing downstream knows
 * or cares which is in use.
 */

export type JobStreamStatus =
  | 'idle'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'rejected'
  | 'expired'
  | 'pending-approval';

export interface JobStreamState {
  readonly status: JobStreamStatus;
  readonly rowsRead: number | null;
  readonly phase: string | null;
  readonly error: { code: string; message: string } | null;
  readonly hasResult: boolean;
}

interface JobPayload {
  status: JobStreamStatus;
  rowCount: number | null;
  hasResult: boolean;
  error: { code: string; message: string } | null;
}

const TERMINAL: ReadonlySet<JobStreamStatus> = new Set([
  'succeeded',
  'failed',
  'cancelled',
  'rejected',
  'expired',
]);

export interface UseJobStreamOptions {
  readonly enabled?: boolean;
  /** Called once, when the job reaches a terminal state. */
  readonly onDone?: (jobId: string, status: JobStreamStatus) => void;
}

export function useJobStream(
  jobId: string | null,
  options: UseJobStreamOptions = {},
): JobStreamState {
  const { enabled = true, onDone } = options;

  const [state, setState] = useState<JobStreamState>({
    status: 'idle',
    rowsRead: null,
    phase: null,
    error: null,
    hasResult: false,
  });

  /**
   * `onDone` is read through a ref so a caller passing an inline closure
   * does not tear down and re-establish the stream on every render -- which
   * would reconnect continuously and never deliver anything.
   */
  const doneRef = useRef(onDone);
  useEffect(() => {
    doneRef.current = onDone;
  });

  /** Guards against firing `onDone` twice when the poll and stream race. */
  const firedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!jobId || !enabled) {
      setState({ status: 'idle', rowsRead: null, phase: null, error: null, hasResult: false });
      return;
    }

    firedRef.current = null;
    const controller = new AbortController();
    let stopped = false;

    const settle = (job: JobPayload): boolean => {
      setState((previous) => ({
        status: job.status,
        // The row count from a completed job supersedes progress pings; a
        // final count that jumped backwards to the last ping would look
        // like data was lost.
        rowsRead: job.rowCount ?? previous.rowsRead,
        phase: TERMINAL.has(job.status) ? null : previous.phase,
        error: job.error,
        hasResult: job.hasResult,
      }));

      if (TERMINAL.has(job.status) && firedRef.current !== jobId) {
        firedRef.current = jobId;
        doneRef.current?.(jobId, job.status);
        return true;
      }
      return false;
    };

    /**
     * Polling runs in both modes, not only as a fallback.
     *
     * A NOTIFY published while the server's listener was reconnecting is
     * lost -- LISTEN/NOTIFY has no replay. Without the poll, a job that
     * completed during a momentary blip leaves the spinner running
     * forever. It backs off once the stream is proven live.
     */
    const poll = async (): Promise<void> => {
      while (!stopped) {
        try {
          const job = await api.get<JobPayload>(`/api/jobs/${jobId}`, {
            signal: controller.signal,
          });
          if (settle(job)) return;
        } catch {
          // Transient. The next tick retries; surfacing a failed poll as a
          // job failure would be a lie about the job.
        }
        await delay(appConfig().sse.enabled ? 5_000 : 1_500, controller.signal);
      }
    };

    void poll();

    if (appConfig().sse.enabled) {
      void openEventStream(
        `/api/jobs/${jobId}/stream`,
        {
          onEvent: ({ type, data }) => {
            if (type === 'status') {
              settle(data as JobPayload);
              return;
            }
            if (type === 'progress') {
              const progress = data as { phase?: string; rowsRead?: number };
              setState((previous) => ({
                ...previous,
                status: previous.status === 'idle' ? 'running' : previous.status,
                phase: progress.phase ?? previous.phase,
                rowsRead: progress.rowsRead ?? previous.rowsRead,
              }));
            }
          },
          onError: () => {
            // Silent. The poll is already running and will carry the job to
            // completion; telling the user the stream dropped would be
            // reporting an implementation detail they cannot act on.
          },
        },
        controller.signal,
      ).catch(() => undefined);
    }

    return () => {
      stopped = true;
      controller.abort();
    };
  }, [jobId, enabled]);

  return state;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    function done(): void {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done, { once: true });
  });
}
